import SchemaOrg from '@/components/base/SchemaOrg';
import GuideLayout from '@/pages/guide/components/GuideLayout';
import { Link } from 'react-router-dom';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Superbuy Sheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Superbuy Sheet is an organized product discovery platform that consolidates curated shopping resources across fourteen fashion and lifestyle categories. It provides structured access to thousands of products, helping shoppers discover and compare items more efficiently than traditional marketplace browsing.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Superbuy Sheet free to use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, completely free. There are no subscription fees, no registration requirements, and no hidden costs for browsing categories and discovering products. Purchases are handled through our main website at kaspreadsheet.com.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need an account to use Superbuy Sheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No account is required for browsing and product discovery. You can explore all fourteen categories and access all informational guides without any registration.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is Superbuy Sheet different from regular online shopping?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Traditional shopping platforms scatter products across countless pages with algorithm-driven recommendations. Superbuy Sheet organizes everything into fourteen clear categories with direct links, making discovery faster and more intentional.',
      },
    },
    {
      '@type': 'Question',
      name: 'How many product categories are available on Superbuy Sheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'There are fourteen categories: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff — covering virtually every fashion and lifestyle need.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Superbuy Sheet safe to use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The platform does not collect personal data or process payments. All product links direct to kaspreadsheet.com, which maintains standard security protocols. For more details, see our Safety Guide.',
      },
    },
  ],
};

export default function FAQPage() {
  return (
    <>
      <SchemaOrg data={faqSchema} />
      <GuideLayout
        title="Superbuy Sheet FAQ: Answers to Your Most Common Questions"
        description="Find answers to frequently asked questions about Superbuy Sheet. Covers platform usage, safety, product discovery, shipping, and everything shoppers want to know."
        breadcrumbLabel="FAQ"
        readTime="8 min read"
        imageUrl="https://readdy.ai/api/search-image?query=Friendly%20minimalist%20photography%20of%20an%20open%20notebook%20with%20handwritten%20question%20marks%20and%20answered%20checklist%20next%20to%20a%20laptop%20showing%20help%20interface%2C%20warm%20cream%20background%2C%20soft%20natural%20lighting%2C%20sage%20green%20pen%20and%20coffee%20cup%2C%20organized%20helpful%20atmosphere%2C%20editorial%20style%2C%20high%20detail&width=1600&height=700&seq=guide-faq-img-p01&orientation=landscape"
        imageAlt="Superbuy Sheet frequently asked questions"
        internalLinks={[
          { label: 'Complete Guide', to: '/guide', icon: 'ri-book-open-line' },
          { label: 'Beginner Guide', to: '/guide/beginner', icon: 'ri-compass-line' },
          { label: 'Safety Guide', to: '/guide/safety', icon: 'ri-shield-check-line' },
          { label: 'How to Use', to: '/guide/how-to-use', icon: 'ri-guide-line' },
          { label: 'Shipping Guide', to: '/guide/shipping', icon: 'ri-ship-line' },
        ]}
      >
        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Everything You Want to Know About Superbuy Sheet
          </h2>
          <p>
            This comprehensive FAQ covers the most common questions about
            <strong className="text-foreground-950"> Superbuy Sheet</strong>, from basic platform
            functionality to advanced usage tips. Whether you are a first-time visitor or a regular
            user looking to deepen your understanding, you will find clear, actionable answers here.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            General Questions
          </h2>
          <div className="space-y-4">
            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">What is Superbuy Sheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Superbuy Sheet is an organized product discovery platform that consolidates curated
                shopping resources across fourteen fashion and lifestyle categories. It provides structured
                access to thousands of products, helping shoppers discover and compare items more efficiently
                than traditional marketplace browsing.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">Is Superbuy Sheet free to use?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Yes, completely free. There are no subscription fees, no registration requirements, and
                no hidden costs for browsing categories and discovering products. Purchases are handled
                through our main website at kaspreadsheet.com, where standard marketplace terms apply.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">Do I need an account to use Superbuy Sheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                No account is required for browsing and product discovery. You can explore all fourteen
                categories and access all informational guides without any registration.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">How is Superbuy Sheet different from regular online shopping?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Traditional shopping platforms scatter products across countless pages with algorithm-driven
                recommendations. Superbuy Sheet organizes everything into fourteen clear categories with
                direct links, making discovery faster and more intentional. You control what you see rather
                than being fed recommendations.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Product &amp; Category Questions
          </h2>
          <div className="space-y-4">
            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">How many product categories are available?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                There are fourteen categories: Shoes, Hoodies &amp; Sweaters, T-Shirts, Jackets,
                Pants &amp; Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts,
                Perfumes, and Other Stuff — covering virtually every fashion and lifestyle need.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">How often are products updated?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Product collections are refreshed regularly across all categories. New arrivals, seasonal
                highlights, and trending items are added frequently to ensure fresh discoveries with every visit.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">Can I find specific brands on Superbuy Sheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Product listings on Superbuy Sheet link to our main platform at kaspreadsheet.com, where
                detailed brand and product information is available. Browse by category first, then explore
                specific brands on the main product pages.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Safety &amp; Trust Questions
          </h2>
          <div className="space-y-4">
            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">Is Superbuy Sheet safe to use?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Yes. The platform does not collect personal data or process payments. All product links
                direct to kaspreadsheet.com, which maintains standard security protocols. For more details,
                see our <Link to="/guide/safety" className="text-primary-500 hover:text-primary-600">Safety Guide</Link>.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">Is Superbuy Sheet affiliated with Superbuy officially?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                No. Superbuy Sheet is an independent informational and navigational platform. We are not
                affiliated with Superbuy officially. Our role is to provide organized product discovery
                that connects shoppers to our main platform at kaspreadsheet.com.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h3 className="font-semibold text-foreground-950 mb-2">What should I do if I have an issue with a purchase?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                All purchase-related inquiries should be directed to kaspreadsheet.com, where you will
                find customer support resources and order management tools. Superbuy Sheet is a discovery
                platform and does not handle purchase transactions directly.
              </p>
            </div>
          </div>
        </section>

        <section className="text-center py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-base rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Main Website
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
          </div>
        </section>
      </GuideLayout>
    </>
  );
}